import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Box,
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    Card,
    CardContent,
    Chip,
    Avatar,
    List,
    Badge,
    CircularProgress,
    Tab,
    Tabs
} from '@mui/material'
import {
    Logout as LogoutIcon,
    LocationOn as LocationIcon,
    ChevronRight as ChevronRightIcon,
    DeliveryDining as RiderIcon
} from '@mui/icons-material'
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore'
import { getFirebaseDb } from '../../config/firebase'
import { useAuth } from '../../contexts/AuthContext'
import { formatCurrency, getOrderStatusColor } from '../../utils'
import type { Order } from '../../types'

export default function DashboardPage() {
    const [orders, setOrders] = useState<Order[]>([])
    const [loading, setLoading] = useState(true)
    const [tab, setTab] = useState(0)

    const { currentUser, logout } = useAuth()
    const navigate = useNavigate()
    const db = getFirebaseDb()

    useEffect(() => {
        if (!currentUser?.uid) return

        const ordersQuery = query(
            collection(db, 'orders'),
            where('delivery.deliveryBoyId', '==', currentUser.uid),
            orderBy('createdAt', 'desc')
        )

        const unsubscribe = onSnapshot(ordersQuery, (snapshot) => {
            const orderData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Order[]
            setOrders(orderData)
            setLoading(false)
        })

        return () => unsubscribe()
    }, [db, currentUser?.uid])

    const handleLogout = async () => {
        await logout()
        navigate('/login')
    }

    const activeOrders = orders.filter(o => ['confirmed', 'processing', 'shipped'].includes(o.status))
    const completedOrders = orders.filter(o => o.status === 'delivered')

    const displayedOrders = tab === 0 ? activeOrders : completedOrders

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
            {/* App Bar */}
            <AppBar
                position="sticky"
                elevation={0}
                sx={{
                    backgroundColor: 'white',
                    color: 'text.primary',
                    borderBottom: '1px solid #e2e8f0'
                }}
            >
                <Toolbar>
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 2,
                        }}
                    >
                        <RiderIcon sx={{ color: 'white', fontSize: 24 }} />
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {currentUser?.displayName || 'Rider'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'grey.500' }}>
                            Active Rider
                        </Typography>
                    </Box>
                    <IconButton onClick={handleLogout} color="inherit">
                        <LogoutIcon />
                    </IconButton>
                </Toolbar>
            </AppBar>

            {/* Stats */}
            <Box sx={{ p: 2, display: 'flex', gap: 2 }}>
                <Card sx={{ flex: 1, background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant="h3" sx={{ color: 'white', fontWeight: 700 }}>
                            {activeOrders.length}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                            Active
                        </Typography>
                    </CardContent>
                </Card>
                <Card sx={{ flex: 1 }}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant="h3" sx={{ fontWeight: 700, color: 'grey.700' }}>
                            {completedOrders.length}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'grey.500' }}>
                            Completed
                        </Typography>
                    </CardContent>
                </Card>
            </Box>

            {/* Tabs */}
            <Box sx={{ px: 2 }}>
                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    sx={{
                        mb: 2,
                        '& .MuiTab-root': {
                            fontWeight: 600,
                            textTransform: 'none',
                            minWidth: 'auto',
                            px: 3
                        }
                    }}
                >
                    <Tab
                        label={
                            <Badge badgeContent={activeOrders.length} color="primary">
                                <Box sx={{ pr: 2 }}>Active</Box>
                            </Badge>
                        }
                    />
                    <Tab label="Completed" />
                </Tabs>
            </Box>

            {/* Orders List */}
            <Box sx={{ px: 2, pb: 4 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress color="primary" />
                    </Box>
                ) : displayedOrders.length === 0 ? (
                    <Card>
                        <CardContent sx={{ textAlign: 'center', py: 6 }}>
                            <Typography variant="body1" sx={{ color: 'grey.500' }}>
                                {tab === 0 ? 'No active deliveries' : 'No completed deliveries yet'}
                            </Typography>
                        </CardContent>
                    </Card>
                ) : (
                    <List sx={{ p: 0 }}>
                        {displayedOrders.map((order) => (
                            <Card
                                key={order.id}
                                sx={{ mb: 2, cursor: 'pointer' }}
                                onClick={() => navigate(`/delivery/${order.id}`)}
                            >
                                <CardContent sx={{ p: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                        <Box>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                #{order.invoiceNumber || order.id.slice(0, 8)}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: 'grey.500' }}>
                                                {order.items?.length || 0} items • {formatCurrency(order.pricing?.total || 0)}
                                            </Typography>
                                        </Box>
                                        <Chip
                                            label={order.status}
                                            color={getOrderStatusColor(order.status)}
                                            size="small"
                                            sx={{ textTransform: 'capitalize' }}
                                        />
                                    </Box>

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14 }}>
                                            {order.guestInfo?.name?.charAt(0) || 'G'}
                                        </Avatar>
                                        <Box sx={{ flexGrow: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {order.guestInfo?.name || 'Guest'}
                                            </Typography>
                                        </Box>
                                        <ChevronRightIcon sx={{ color: 'grey.400' }} />
                                    </Box>

                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                        <LocationIcon sx={{ fontSize: 18, color: 'grey.500', mt: 0.3 }} />
                                        <Typography variant="body2" sx={{ color: 'grey.600', flex: 1 }}>
                                            {order.deliveryAddress?.street}, {order.deliveryAddress?.city}
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        ))}
                    </List>
                )}
            </Box>
        </Box>
    )
}
